
import {
  defineComponent, ref, Ref, computed, watch,
} from 'vue';
import {
  APIOutput,
  APIConverted,
  APIVerified,
  APIUnverified,
  APIAudited,
  AuditT,
  Tab,
  resultFilterer,
  getQueryResult,
  APIOutputT,
  APIText,
  delist,
  getTitle,
  getCreator,
  getDate,
  getPublisher,
  getHebrewTitle,
} from '../lib/datastructures';
import { applyQuery } from '../lib/nli_helpers';

interface RepContainer {
  currKey: string;
  currRep: string;
  reps: string[];
  hot: boolean;
  idx: number;
}

export default defineComponent({
  name: 'Audit',
  setup() {
    const textRecords = ref('');
    const recordsObject: Ref<APIOutput[] | string> = ref('Enter records to begin!');

    watch(textRecords,
      (newValue) => {
        let text = newValue.trim();
        if (text === '' || text === '[]') {
          recordsObject.value = 'Enter some records to begin!';
          window.localStorage.setItem('textRecords', text);
          return;
        }
        let start = text[0];
        if (start === '[' || start === ',') {
          start = '[';
        } else {
          start = `[${start}`;
        }
        let end = text.slice(-1);
        if (end === ']' || end === ',') {
          end = ']';
        } else {
          end = `${end}]`;
        }
        text = `${start}${text.slice(1, -1)}${end}`;
        window.localStorage.setItem('textRecords', text);
        try {
          recordsObject.value = JSON.parse(text);
        } catch (e) {
          recordsObject.value = 'Record input was invalid JSON';
        }
      });
    const currTab = ref(Tab.Input);

    const makeRepContainers = (replists: APIText): RepContainer[] => replists.output.map(
      (replist, i) => ({
        currKey: replist.key,
        currRep: replist.reps[0],
        reps: replist.reps.slice(0, 10),
        hot: false,
        idx: i,
      }),
    );

    const toggleRef = (someRef: Ref<boolean>) => {
      /* eslint-disable no-param-reassign */
      someRef.value = !(someRef.value);
    };

    const showSimpleEditor = ref(true);
    const toggleSimpleEditor = () => toggleRef(showSimpleEditor);
    const simpleEditorText = ref('');

    const audited: Ref<APIAudited[]> = ref([]);
    const saveAudited = () => window.localStorage
      .setItem('audited', JSON.stringify(audited.value));

    const pushAudited = (value: APIAudited) => {
      const out = audited.value.push(value);
      saveAudited();
      return out;
    };

    const popAudited = () => {
      const out = audited.value.pop();
      saveAudited();
      return out;
    };

    const repContainers: Ref<RepContainer[] | null> = ref(null);
    const currentRecord: Ref<string | APIOutput> = ref('');
    watch(recordsObject, (records) => {
      repContainers.value = null;
      showSimpleEditor.value = true;

      if (typeof records === 'string') {
        currentRecord.value = records;
        return;
      }
      if (recordsObject.value.length === 0) {
        currTab.value = audited.value.length === 0 ? Tab.Audit : Tab.Input;

        currentRecord.value = 'No more records. Please enter some.';
        return;
      }
      const req = new XMLHttpRequest();
      req.onload = () => {
        let replists;
        try {
          replists = JSON.parse(req.responseText) as APIText;
        } catch (e) {
          console.log("didn't get valid JSON from the server");
          return;
        }
        repContainers.value = makeRepContainers(replists);
        showSimpleEditor.value = false;
      };
      req.open('GET', `/text/${encodeURI(getTitle(records[0]))}`);
      req.send();

      simpleEditorText.value = getHebrewTitle(records[0]);
      [currentRecord.value] = records;
    });
    const currentConverted = currentRecord as Ref<APIConverted>;

    const clearInput = () => {
      textRecords.value = '';
    };

    const currentHebrewTitle = computed(() => {
      if (typeof currentRecord.value === 'string') { return ''; }
      return getHebrewTitle(currentRecord.value);
    });

    // eslint-disable-next-line
    // const replists: Ref<APIText> = ref({"type": "text", "output": [{"key": "ʿosḳim", "reps": ["עוסקים", "עוסקם", "עסקים", "עסקם", "עוסקאם", "עאסקים", "עאסקם", "עסקאם", "עאסקאם"]}, {"key": "u-fidyon", "reps": ["ופדיון", "ופדין", "ופידיין", "ופידין", "ופדייאן", "ופדיאן", "ופידייאן", "ופידיאן", "ופדייון", "ופידייון", "ופידיון", "ופדיין"]}, {"key": "be-ʿanfe", "reps": ["בענפי", "בענפא", "בענפה", "בעאנפי", "בעאנפא", "בעאנפה"]}, {"key": "ha-mesheḳ", "reps": ["המשק", "המשיק", "המישק", "המישיק", "המשאק", "המאשק", "המישאק", "המאשיק", "המאשאק"]}, {"key": "ʿal", "reps": ["על", "אל", "עאל"]}, {"key": "pe", "reps": ["פה", "פי", "פא"]}, {"key": "mas", "reps": ["מס", "מאס"]}, {"key": "ʿerekh", "reps": ["ערך", "עריך", "עירך", "עיריך", "עראך", "עארך", "עיראך", "עאריך", "עאראך"]}, {"key": "musaf", "reps": ["מוסף", "מסף", "מסאף", "מוסאף"]}], "diagnostic_info": {"standard": APIStandard.NewDIN, "foreign_tokens": false, "transliteration_tokens": true, "fully_converted": true, "all_cached": false, "all_recognized": true}})
    // eslint-disable-next-line
    // const replists = {"type": "text", "output": [{"key": "sefirot", "reps": ["ספירות", "ספרות", "סיפירות", "סיפרות", "ספירת", "ספרת", "סיפירת", "סיפרת", "ספיראת", "ספארות", "סאפירות", "ספראת", "סיפיראת", "סיפארות", "סאפרות", "סיפראת", "ספארת", "סאפירת", "סיפארת", "סאפרת"]}, {"key": "tenuʿah", "reps": ["תנועה", "תנעה", "תאנועה", "תינעה", "טנועה", "טינועה", "תאנעה", "טנעה", "טאנועה", "טינעה", "טאנעה", "תינועה"]}, {"key": "ba-derakhim", "reps": ["בדרכים", "בדרכם", "בדירכים", "בדירכם", "בדרכאם", "בדראכים", "בדארכים", "בדראכם", "בדירכאם", "בדיראכים", "בדארכם", "בדיראכם", "בדראכאם", "בדארכאם", "בדאראכים", "בדיראכאם", "בדאראכם", "בדאראכאם"]}, {"key": "lo", "reps": ["לא", "לו", "לוא"]}, {"key": "-", "reps": ["-"]}, {"key": "ʿironiyot", "reps": ["עירוניות", "ערונייות", "ערוניות", "עירוניית", "עירנייות", "עירונית", "עירניות", "ערוניית", "ערנייות", "ערונית", "ערניות", "עירניית", "עירנית", "ערניית", "ערנית", "עירונייאת", "עיראנייות", "עארונייות", "עירוניאת", "עיראניות"]}], "diagnostic_info": {"standard": "New DIN 31631", "foreign_tokens": false, "transliteration_tokens": true, "fully_converted": true, "all_cached": false, "all_recognized": true}};
    let currentlyHot = 0;
    const activateEditor = (word: RepContainer) => {
      if (repContainers.value === null) { return; }
      repContainers.value[currentlyHot].hot = false;
      // eslint-disable-next-line
      word.hot = true;
      currentlyHot = word.idx;
    };
      /* eslint-disable no-param-reassign */
    const updateIdxs = (rcs: RepContainer[]) => rcs.forEach((word, i) => { word.idx = i; });
    const deleteWord = (word: RepContainer) => {
      if (repContainers.value === null) { return; }
      repContainers.value = repContainers.value.filter((_, i) => i !== word.idx);
      currentlyHot = 0;
      updateIdxs(repContainers.value);
    };
    const addWord = (word: RepContainer) => {
      if (repContainers.value === null) { return; }
      word.hot = false;
      repContainers.value.splice(word.idx + 1, 0, {
        currKey: '(added word)',
        currRep: '',
        reps: [''],
        hot: true,
        idx: 0,
      });
      updateIdxs(repContainers.value);
    };
    const moveWord = (word: RepContainer, inc: number) => {
      if (repContainers.value === null) { return; }
      const newPosition = word.idx + inc;
      if (newPosition >= repContainers.value.length || newPosition < 0) { return; }
      const other = repContainers.value[newPosition];
      repContainers.value[word.idx] = other;
      repContainers.value[newPosition] = word;
      updateIdxs(repContainers.value);
    };
    const isActive = (tab: Tab) => (currTab.value === tab ? 'active' : '');
    const boxSize = (word: RepContainer, ratio: number): string => (
      `max-width: ${Math.max(word.currRep.length, 2) * ratio}rem`
    );

    const topQuery = computed(() => {
      const current = currentRecord.value;
      if (typeof current === 'string' || current.type === 'error') {
        return { text: '', link: '' };
      }
      return getQueryResult(current as (APIVerified | APIUnverified));
    });
    const corrected = ref('');
    watch(currentRecord, (newValue) => {
      if (typeof newValue !== 'string' && newValue.type !== 'error') {
        corrected.value = (newValue as APIConverted).converted;
      }
    });

    const loadFile = (event: Event | null) => {
      if (event === null) { return; }
      const { target } = event;
      const { files } = target as HTMLInputElement;
      if (files === null || files === undefined) { return; }
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        if (typeof reader.result === 'string') {
          textRecords.value = reader.result;
        }
      });
      reader.readAsText(files[0]);
    };

    const showFullRecord = ref(false);
    const toggleFullRecord = () => toggleRef(showFullRecord);
    const showCorrectionInterface = ref(false);
    const toggleCorrectionInterface = () => toggleRef(showCorrectionInterface);
    const showAdvancedFilters = ref(false);
    const toggleAdvancedFilters = () => toggleRef(showAdvancedFilters);

    const filVerified = ref(false);
    const filUnverified = ref(true);
    const filError = ref(false);
    const filDisplay = ref(true);
    const filSearch = ref(true);
    const filNoRecommendation = ref(true);
    const filLikelyTranscription = ref(true);

    const filterResults = computed(
      () : [APIOutput[], APIOutput[]] | null => {
        if (typeof recordsObject.value === 'string') {
          return null;
        }
        const result: APIOutput[] = [];
        const removed: APIOutput[] = [];
        recordsObject.value.forEach((el) => {
          if (resultFilterer(
            el,
            filVerified.value,
            filUnverified.value,
            filError.value,
            filDisplay.value,
            filSearch.value,
            filNoRecommendation.value,
            filLikelyTranscription.value,
          )) {
            result.push(el);
          } else {
            removed.push(el);
          }
        });
        return [result, removed];
      },
    );

    const getExportURL = (obj: APIOutput[], urlVar: string[]) => {
      const data = new Blob(
        [JSON.stringify(obj)], { type: 'application/json' },
      );
      if (urlVar.length !== 0) {
        URL.revokeObjectURL(urlVar[0]);
      }
      urlVar[0] = URL.createObjectURL(data);
      return urlVar[0];
    };

    const remainingURL: string[] = [];
    const exportRemaining = () => (
      filterResults.value === null ? '' : getExportURL(filterResults.value[0], remainingURL)
    );

    const removedURL: string[] = [];
    const exportRemoved = () => (
      filterResults.value === null ? '' : getExportURL(filterResults.value[1], removedURL)
    );

    const applyFilters = () => {
      if (filterResults.value === null) {
        return;
      }
      // eslint-disable-next-line
      textRecords.value = JSON.stringify(filterResults.value[0]);
    };

    const keepResultText = computed(() => {
      if (filterResults.value === null) {
        return '';
      }
      return JSON.stringify(filterResults.value[0]);
    });

    const auditedURL: string[] = [];
    const exportAudited = () => getExportURL(audited.value, auditedURL);
    const clearAudited = () => {
      audited.value = [];
      saveAudited();
    };
    const popRecord = () => {
      if (typeof recordsObject.value === 'string') {
        throw Error('No Records');
      }
      const [record] = recordsObject.value;
      recordsObject.value = recordsObject.value.slice(1);
      textRecords.value = JSON.stringify(recordsObject.value);
      return record;
    };

    const undo = () => {
      if (typeof recordsObject.value === 'string') { return; }
      const last = popAudited();
      if (last === undefined) { return; }

      recordsObject.value.splice(0, 0, last);
      textRecords.value = JSON.stringify(recordsObject.value);
    };

    const accept = () => {
      const auditRecord = popRecord() as APIAudited;
      const title = getHebrewTitle(auditRecord);
      let type: AuditT;
      if (auditRecord.type === APIOutputT.Verified) {
        type = AuditT.MatchedTitle;
      } else if (auditRecord.type === APIOutputT.Unverified) {
        type = AuditT.Converted;
      } else {
        return;
      }
      auditRecord.audit_result = { type, title };
      pushAudited(auditRecord);
    };

    const acceptEdited = () => {
      if (repContainers.value === null) {
        throw Error("don't accept edited with no input");
      }
      let title;
      if (showSimpleEditor.value === true) {
        title = simpleEditorText.value;
      } else {
        title = repContainers.value.map((rc) => rc.currRep).join(' ');
      }
      const auditRecord = popRecord() as APIAudited;
      auditRecord.audit_result = { type: AuditT.Edited, title };
      pushAudited(auditRecord);
    };

    const acceptTopQuery = () => {
      const record = popRecord();
      if (record.type !== 'unverified') {
        undo();
        throw Error('should only be possible to reach this code with unverified record.');
      }
      const unverified = record as APIUnverified;
      const title = delist(unverified.top_query_result.text);
      const auditRecord = record as APIAudited;
      auditRecord.audit_result = { type: AuditT.TopQuery, title };
      pushAudited(auditRecord);
      popRecord();
    };
    const reject = () => {
      const auditRecord = popRecord() as APIAudited;
      auditRecord.audit_result = { type: AuditT.Rejected, title: '' };
      pushAudited(auditRecord);
    };

    if (audited.value.length === 0) {
      const temp = window.localStorage.getItem('audited');
      if (temp !== null) {
        audited.value = JSON.parse(temp);
      }
    }
    if (textRecords.value === '') {
      const temp = window.localStorage.getItem('textRecords');
      if (temp !== null) {
        textRecords.value = temp;
      }
    }

    return {
      makeRepContainers,
      textRecords,
      recordsObject,
      currTab,
      isActive,
      Tab,
      currentRecord,
      currentConverted,
      loadFile,
      corrected,
      clearAudited,
      clearInput,
      accept,
      acceptEdited,
      acceptTopQuery,
      reject,
      undo,
      audited,
      showFullRecord,
      toggleRef,
      toggleFullRecord,
      showCorrectionInterface,
      showSimpleEditor,
      toggleSimpleEditor,
      simpleEditorText,
      toggleCorrectionInterface,
      deleteWord,
      addWord,
      moveWord,
      showAdvancedFilters,
      toggleAdvancedFilters,
      getTitle,
      getDate,
      getCreator,
      getPublisher,
      getHebrewTitle,
      currentHebrewTitle,
      filVerified,
      filUnverified,
      filError,
      filDisplay,
      filSearch,
      filNoRecommendation,
      filLikelyTranscription,
      filterResults,
      exportRemaining,
      exportRemoved,
      exportAudited,
      applyFilters,
      keepResultText,
      topQuery,
      delist,
      repContainers,
      applyQuery,
      activateEditor,
      boxSize,
    };
  },
});
